import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';

import { getCheckForPipelineConfigurationUrl } from 'src/components/pipelines/components/ai/components/utils';
import { loadAgenticPipelinesConfiguration } from 'src/components/pipelines/resources/agentic-pipelines-configuration';
import {
  getCurrentRepositoryFullSlug,
  getRepositoryMainBranch,
} from 'src/selectors/repository-selectors';
import { authRequest, jsonHeaders } from 'src/utils/fetch';

import { FIX_FLAKY_PIPELINE_TRIGGER_TYPE } from '../constants';

export type UseIsFixFlakyAvailableResult = {
  isFixFlakyAvailableLoading: boolean;
  isFixFlakyAvailable: boolean;
};

export function useIsFixFlakyAvailable(): UseIsFixFlakyAvailableResult {
  const repositoryFullSlug = useSelector(getCurrentRepositoryFullSlug);
  const mainBranch = useSelector(getRepositoryMainBranch);
  const {
    data: agenticPipelinesConfiguration,
    loading: agenticPipelinesEnabledLoading,
  } = useResource(loadAgenticPipelinesConfiguration);
  const agenticPipelinesEnabled = agenticPipelinesConfiguration?.enabled;
  const [checkLoading, setCheckLoading] = useState(true);
  const [isFixFlakyTriggerConfigured, setIsFixFlakyTriggerConfigured] =
    useState(false);

  useEffect(
    function checkIfFixFlakyTriggerConfigured() {
      if (
        !repositoryFullSlug ||
        !mainBranch?.name ||
        !mainBranch?.target?.hash ||
        agenticPipelinesEnabledLoading ||
        !agenticPipelinesEnabled
      ) {
        setCheckLoading(false);
        setIsFixFlakyTriggerConfigured(false);
        return undefined;
      }

      let cancelled = false;
      setCheckLoading(true);

      (async () => {
        try {
          const url = getCheckForPipelineConfigurationUrl(repositoryFullSlug);
          const body = {
            trigger: { type: FIX_FLAKY_PIPELINE_TRIGGER_TYPE },
            target: {
              type: 'pipeline_ref_target',
              ref_type: 'branch',
              ref_name: mainBranch.name,
              commit: { hash: mainBranch.target.hash },
            },
          };

          const response = await fetch(
            authRequest(url, {
              method: 'POST',
              headers: jsonHeaders,
              body: JSON.stringify(body),
            })
          );

          if (cancelled) return;

          setIsFixFlakyTriggerConfigured(response.ok);
        } catch {
          if (!cancelled) {
            setIsFixFlakyTriggerConfigured(false);
          }
        } finally {
          if (!cancelled) {
            setCheckLoading(false);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    },
    [
      repositoryFullSlug,
      mainBranch,
      agenticPipelinesEnabled,
      agenticPipelinesEnabledLoading,
    ]
  );

  const isFixFlakyAvailableLoading =
    checkLoading || agenticPipelinesEnabledLoading;
  const isFixFlakyAvailable =
    !isFixFlakyAvailableLoading &&
    !!agenticPipelinesEnabled &&
    isFixFlakyTriggerConfigured;

  return { isFixFlakyAvailable, isFixFlakyAvailableLoading };
}
